import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Modal, Avatar } from "../components/Layout";
import { fmtDate } from "../lib/dates";
import { RESPONSE_KINDS } from "../lib/bouquet";
import { CalendarDays, Users, Sparkles, Plus, MapPin, UsersRound, Trash2, Sprout, Hand, MessageCircle, Brain, Handshake, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

const KIND_ICONS = { cheer: Hand, check_in: MessageCircle, experience: Brain, help: Handshake, here: Heart };

export default function Community() {
  const [data, setData] = useState(null);
  const [showGathering, setShowGathering] = useState(false);
  const [gForm, setGForm] = useState({ title: "", purpose: "", date: "", time: "", location: "", link: "", image_path: "" });
  const [gUploading, setGUploading] = useState(false);
  const [gPreview, setGPreview] = useState("");

  const load = () => { api.get("/community").then((r) => setData(r.data)).catch(() => {}); };
  useEffect(load, []);

  const rsvp = async (g, status) => {
    await api.post(`/gatherings/${g.event_id}/rsvp`, { status });
    toast.success("RSVP saved");
    load();
  };

  const respond = async (s, kind) => {
    await api.post(`/steps/${s.step_id}/respond`, { kind });
    load();
  };

  const pickImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setGUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/uploads/image", fd);
      setGForm((prev) => ({ ...prev, image_path: res.data.path }));
      setGPreview(`${api.defaults.baseURL}/files/${res.data.path}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setGUploading(false);
    }
  };

  const createGathering = async () => {
    if (!gForm.title.trim() || !gForm.date) return toast.error("Title and date are needed");
    try {
      await api.post("/community/gatherings", gForm);
      toast.success("Gathering scheduled");
      setShowGathering(false);
      setGForm({ title: "", purpose: "", date: "", time: "", location: "", link: "", image_path: "" });
      setGPreview("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create gathering");
    }
  };

  const removeMoment = async (m) => {
    await api.delete(`/moments/${m.moment_id}`);
    toast.success("Moment removed");
    load();
  };

  if (!data) return <div className="animate-pulse text-muted-foreground text-sm">Gathering your community…</div>;

  const c = data.community;
  if (!c) {
    return (
      <div className="bg-card border border-border rounded-xl p-10 text-center space-y-3" data-testid="community-empty">
        <UsersRound className="h-8 w-8 text-primary mx-auto" />
        <p className="font-display text-xl font-medium">No community yet</p>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">Your community comes from how you joined Bouquet. Once you're part of one, it will live here.</p>
      </div>
    );
  }

  return (
    <div data-testid="community-page" className="space-y-8">
      <PageHeader eyebrow="Community" title={c.name} description={c.description || "The wider circle you're part of."}>
        <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-muted-foreground">
          {c.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {c.city}</span>}
          <span className="flex items-center gap-1" data-testid="community-member-count"><Users className="h-3.5 w-3.5" /> {c.member_count} members</span>
        </div>
      </PageHeader>

      <section className="space-y-4" data-testid="community-gatherings-section">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-medium flex items-center gap-2"><CalendarDays className="h-5 w-5 text-primary" /> Community Gatherings</h3>
          <button data-testid="create-gathering-button" onClick={() => setShowGathering(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-border text-xs font-semibold hover:bg-secondary transition-colors">
            <Plus className="h-3.5 w-3.5" /> Plan one
          </button>
        </div>
        {data.gatherings.length === 0 && <p className="text-sm text-muted-foreground">Nothing on the calendar — anyone in the community can plan one.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.gatherings.map((g) => (
            <div key={g.event_id} className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid={`gathering-card-${g.event_id}`}>
              {g.image_path && (
                <img src={`${api.defaults.baseURL}/files/${g.image_path}`} alt={g.title} className="w-full h-32 object-cover rounded-lg" data-testid={`gathering-image-${g.event_id}`} />
              )}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-sm">{g.title}</p>
                  {g.purpose && <p className="text-xs text-muted-foreground mt-0.5">{g.purpose}</p>}
                </div>
                <p className="text-xs font-semibold text-primary shrink-0">{fmtDate(g.date)}{g.time ? ` · ${g.time}` : ""}</p>
              </div>
              {g.location && <p className="text-xs text-muted-foreground">{g.location}</p>}
              {g.link && <a href={g.link} target="_blank" rel="noreferrer" data-testid={`gathering-link-${g.event_id}`}
                className="text-xs font-semibold text-primary hover:underline block">Event link →</a>}
              <div className="grid grid-cols-3 gap-2">
                {[["going", "Going"], ["maybe", "Maybe"], ["cant", "Can't make it"]].map(([key, label]) => (
                  <button key={key} data-testid={`rsvp-${key}-${g.event_id}`} onClick={() => rsvp(g, key)}
                    className={`px-2 py-2 rounded-lg text-[11px] font-semibold transition-colors ${g.my_rsvp === key ? "bg-primary text-primary-foreground" : "border border-border hover:bg-secondary"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">{g.going_count} going</p>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4" data-testid="community-moments-section">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-medium flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Community Moments</h3>
          <Link to="/moments" data-testid="goto-moments" className="text-xs font-semibold text-primary hover:underline">Add via Moments →</Link>
        </div>
        {data.moments.length === 0 && <p className="text-sm text-muted-foreground">No community moments yet — when you preserve a moment, choose to share it with {c.name}.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.moments.map((m) => (
            <div key={m.moment_id} className="bg-card border border-border rounded-xl p-4 space-y-2" data-testid={`community-moment-${m.moment_id}`}>
              {m.image_path && (
                <img src={`${api.defaults.baseURL}/files/${m.image_path}`} alt={m.title} className="w-full h-32 object-cover rounded-lg" />
              )}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{m.title}</p>
                  <p className="text-xs text-muted-foreground">{m.author?.name} · {fmtDate(m.date)}</p>
                </div>
                {m.is_mine && (
                  <button data-testid={`community-moment-delete-${m.moment_id}`} onClick={() => removeMoment(m)}
                    className="p-1 rounded-lg text-muted-foreground hover:text-destructive transition-colors shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {m.description && <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{m.description}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4" data-testid="community-steps-section">
        <h3 className="font-display text-xl font-medium flex items-center gap-2"><Sprout className="h-5 w-5 text-primary" /> Steps shared with the community</h3>
        {(data.shared_steps || []).length === 0 && <p className="text-sm text-muted-foreground">Nothing shared here yet — when you share a next step from What's Growing, you can also share it with {c.name}.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data.shared_steps || []).map((s) => (
            <div key={s.step_id} className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid={`community-step-${s.step_id}`}>
              <div className="flex items-center gap-2.5">
                <Avatar user={s.author} size="h-8 w-8" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{s.text}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.author?.name} · {s.intention_text}{s.target_date ? ` · by ${fmtDate(s.target_date)}` : ""}</p>
                </div>
              </div>
              {(s.support_preferences || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {s.support_preferences.map((p) => <span key={p} className="px-2 py-0.5 rounded-full bg-secondary text-[10px] font-semibold">{p}</span>)}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">
                {RESPONSE_KINDS.map(({ kind, label }) => {
                  const Icon = KIND_ICONS[kind];
                  const count = (s.responses || []).filter((r) => r.kind === kind).length;
                  return (
                    <button key={kind} data-testid={`community-respond-${kind}-${s.step_id}`} onClick={() => respond(s, kind)}
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-medium border border-border hover:bg-secondary transition-colors">
                      <Icon className="h-3 w-3" /> {label}{count ? ` · ${count}` : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-4" data-testid="community-members-section">
        <h3 className="font-display text-xl font-medium flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Members</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(c.members || []).map((m) => (
            <Link key={m.user_id} to={`/members/${m.user_id}`} data-testid={`community-member-${m.user_id}`}
              className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:border-primary transition-colors">
              <Avatar user={m} size="h-9 w-9" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{m.name}</p>
                <p className="text-xs text-muted-foreground truncate">{m.title}{m.company ? ` · ${m.company}` : ""}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {showGathering && (
        <Modal onClose={() => setShowGathering(false)} testid="gathering-modal">
          <div className="space-y-4">
            <div>
              <h3 className="font-display text-xl font-medium">Plan a community gathering</h3>
              <p className="text-xs text-muted-foreground mt-1">Everyone in {c.name} can see it and RSVP.</p>
            </div>
            <input data-testid="gathering-title-input" value={gForm.title} onChange={(e) => setGForm({ ...gForm, title: e.target.value })}
              placeholder="What are we doing? (e.g. Spring brunch)" className={inputCls} />
            <input data-testid="gathering-purpose-input" value={gForm.purpose} onChange={(e) => setGForm({ ...gForm, purpose: e.target.value })}
              placeholder="Why — optional" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="gathering-date-input" type="date" value={gForm.date} onChange={(e) => setGForm({ ...gForm, date: e.target.value })} className={inputCls} />
              <input data-testid="gathering-time-input" type="time" value={gForm.time} onChange={(e) => setGForm({ ...gForm, time: e.target.value })} className={inputCls} />
            </div>
            <input data-testid="gathering-location-input" value={gForm.location} onChange={(e) => setGForm({ ...gForm, location: e.target.value })}
              placeholder="Where — optional" className={inputCls} />
            <input data-testid="gathering-link-input" value={gForm.link} onChange={(e) => setGForm({ ...gForm, link: e.target.value })}
              placeholder="Link (optional — RSVP page, Zoom, menu…)" className={inputCls} />
            <div>
              <input type="file" accept="image/*" className="hidden" id="gathering-image-upload" data-testid="gathering-image-input" onChange={pickImage} />
              {gPreview ? (
                <img src={gPreview} alt="Gathering" className="w-full h-32 object-cover rounded-lg" data-testid="gathering-image-preview" />
              ) : (
                <label htmlFor="gathering-image-upload" data-testid="gathering-image-label"
                  className="block w-full border border-dashed border-border rounded-lg py-5 text-center text-xs text-muted-foreground hover:bg-secondary transition-colors cursor-pointer">
                  {gUploading ? "Uploading…" : "Add a picture (optional)"}
                </label>
              )}
            </div>
            <button data-testid="gathering-submit" onClick={createGathering}
              className="w-full bg-primary text-primary-foreground py-3 rounded-full text-sm font-semibold">Schedule it</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
